import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCase } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

export default function NewCasePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    case_number: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await createCase(formData);
      toast.success('Case created successfully');
      navigate(`/cases/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="new-case-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6 font-body"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Create New Case</CardTitle>
            <CardDescription className="font-body">
              Add a new case to start uploading documents and analyzing exhibits
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="font-body">Case Title *</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="e.g., Smith v. Johnson - Contract Dispute"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="case-title-input"
                  className="font-body"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_name" className="font-body">Client Name *</Label>
                <Input
                  id="client_name"
                  name="client_name"
                  placeholder="e.g., John Smith"
                  value={formData.client_name}
                  onChange={handleChange}
                  required
                  data-testid="client-name-input"
                  className="font-body"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="case_number" className="font-body">Case Number (Optional)</Label>
                <Input
                  id="case_number"
                  name="case_number"
                  placeholder="e.g., 2024-CV-12345"
                  value={formData.case_number}
                  onChange={handleChange}
                  data-testid="case-number-input"
                  className="font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="font-body">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Brief description of the case and its key issues..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="case-description-input"
                  className="font-body resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="font-body"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-navy hover:bg-navy-light font-body flex-1"
                  disabled={loading}
                  data-testid="create-case-btn"
                >
                  {loading ? 'Creating...' : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create Case
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
